import {
  EVENT_TYPE,
  ORDER_STATUS,
  ORDER_TYPE,
  SUBSCRIPTION_PERIODS,
} from '@/const';
import api from '@/api/api';
import {teamName} from '@/utils/helpers';
import {
  GET_INFO_FOR_ORDER_BY_TYPE,
  GET_PURCHASED_PASSES,
  ORDER_PAY,
} from '../actionTypes';
import {eventTeams} from '@/league/components/EventCards/eventMethods';
import {
  GET_ADDONS_BY_ID,
  GET_PREPARED_PACKAGES_BY_ID,
  GET_REGULARS_BY_ID,
} from '@/store/getterTypes';
import {getFormattedDate} from '@/utils/injections';

const BASE_PRICE_FIELD_NAME = 'price';
const checkPurchased = {
  category: (pack, categoryId) => {
    return pack.preparedCategories
        .some(({id, purchased} = {}) => String(id) === String(categoryId) && purchased);
  },
  attribute: (pack, attributes, categoryId) => {
    const attributeMap = pack.isMultiCategory?
      pack.preparedAttributes :
      pack.preparedCategories.find(({id}) => String(id) === String(categoryId))?.preparedAttributes;

    let exist = false;
    Object.values(attributes).forEach((attrData) => {
      const groupData = attributeMap?.list?.find(
          ({id}) => (String(id) === String(attrData.group.id)),
      );

      if (!groupData) return;
      if (groupData.purchased) {
        exist = true;
        return;
      }
      exist = groupData.values?.find((attr) => String(attr.id) === String(attrData.id))?.purchased;
    });
    return exist;
  },
};

export default {
  [GET_INFO_FOR_ORDER_BY_TYPE]: async ({getters, state, dispatch}, params) => {
    const {
      type,
      id, // package_id, event_id, subscription_id
      single_event_package_id: singleId,
      participant_id: participantId,
      event_category_id: eventCategoryId,
      attributes,
    } = params;

    if (!id) {
      return Promise.reject(new Error('id not selected'));
    }

    if (type === ORDER_TYPE.event) {
      const promise = Promise.allSettled([
        api.eventsApi.get_event({id}),
        singleId && api.paymentApi.package(singleId),
      ]);
      return promise.then(([
        {
          value: event,
          reason: eventReason,
        }, {value: pack}]) => {
        if (eventReason ||
            (event.data?.type === EVENT_TYPE.paid && !singleId)) {
          throw new Error(JSON.stringify(eventReason));
        }

        const isPaid = event?.data?.type === EVENT_TYPE.paid;
        const paymentData = isPaid ? pack?.data : event?.data;
        const teams = eventTeams(event.data);

        const timeObj = getFormattedDate({
          ts: event.data.event_utc_ts,
          timeZone: event.data.time_zone,
          formatsOptions: {
            month: {month: 'short'},
            day: {day: '2-digit'},
            year: {year: 'numeric'},
            hh: {
              hour: '2-digit',
              hour12: false,
              minute: '2-digit',
            },
          },
        });
        const details = {
          time: `${timeObj.month} ${timeObj.day} ${timeObj.year} ${timeObj.hh}`,
          packageName: isPaid ? paymentData.name : '',
        };

        let name;
        if (event.data.participants.length === 2) {
          name = `${teamName(teams.awayTeam.name)} @ ${teamName(
              teams.homeTeam.name)}`;
          details.gender = teams.awayTeam.gender;
          details.sport = teams.awayTeam.category;
          details.team = `${teams.awayTeam.name} & ${teams.homeTeam.name}`;
        } else {
          name = event.data.name;
        }

        const orderData = {
          price: paymentData?.price,
          basePrice: paymentData?.[BASE_PRICE_FIELD_NAME] || paymentData?.price,
          image: paymentData?.image,
          name,
          event_utc_ts: event.data.event_utc_ts,
          exist: event.data.has_access,
        };
        singleId && (orderData.single_event_package_id = singleId);

        const trackData = {
          type: `${ORDER_TYPE.event}${!isPaid ? ' - ' + paymentData.type : ''}`,
          name: isPaid ?
              `${event?.data.name} [package: ${pack?.data?.name}]` :
              event?.data.name,
          id: isPaid ?
              `${event?.data.id} [package: ${pack?.data?.id}]` :
              event?.data.id,
          item: paymentData,
          details,
          event: event.data,
        };
        return {
          trackData,
          orderData,
        };
      });
    }

    await dispatch(GET_PURCHASED_PASSES);

    if (type === ORDER_TYPE.package) {
      const pack = getters[GET_PREPARED_PACKAGES_BY_ID][id];

      if (!pack) {
        throw new Error('Pass not found');
      }

      const exist = pack?.purchased ||
        participantId && pack.disabledParticipants?.includes(Number(participantId)) ||
        eventCategoryId && checkPurchased.category(pack, eventCategoryId) ||
        attributes && checkPurchased.attribute(pack, attributes, eventCategoryId);

      const trackData = {
        type: ORDER_TYPE.package,
        name: pack.name,
        id: pack.id,
        item: pack,
      };

      const orderData = {
        exist,
        price: pack.price,
        basePrice: pack?.[BASE_PRICE_FIELD_NAME] || pack?.price,
        name: pack?.name,
      };

      return {
        trackData,
        orderData,
      };
    }
    if (type === ORDER_TYPE.subscription) {
      const allSubs = {
        ...getters[GET_REGULARS_BY_ID],
        ...getters[GET_ADDONS_BY_ID],
      };
      const subscription = allSubs[id];

      if (!subscription) {
        throw new Error('Pass not found');
      }

      const trackData = {
        type: ORDER_TYPE.subscription,
        name: subscription?.name,
        id: subscription?.id,
        item: subscription,
      };

      const orderData = {
        price: subscription?.price,
        basePrice: subscription?.[BASE_PRICE_FIELD_NAME] ||
            subscription?.price,
        exist: subscription.purchased,
        name: subscription?.name,
        image: subscription?.image,
        period: subscription?.offer === SUBSCRIPTION_PERIODS.ANNUAL ?
            'annual' :
            'monthly',
        trialPeriod: subscription?.trial_period,
      };

      return {
        trackData,
        orderData,
      };
    }
  },
  [ORDER_PAY]: () => {
    const intervalArr = [];
    const clearIntervalCb = (interval) => {
      interval ? clearInterval(interval) : intervalArr.forEach(clearInterval);
    };

    /**
     *
     * @param {number[]} orderIds
     * @return {Promise<unknown>}
     */
    function orderCheck(orderIds) {
      return Promise.allSettled([
        ...orderIds.map((orderId) => {
          return new Promise((resolve, reject) => {
            const interval = setInterval(() => {
              api.paymentApi.getOrderInvoiceStatus({orderId}).then((res) => {
                if ([ORDER_STATUS.paid, ORDER_STATUS.ready].includes(res?.data?.status)) {
                  resolve(res);
                  clearIntervalCb(interval);
                } else if (res?.data?.status === ORDER_STATUS.failed) {
                  reject({
                    ...res?.data,
                    popup: true,
                  });
                  clearIntervalCb(interval);
                }
              }).catch((err) => {
                reject(err);
                clearIntervalCb(interval);
              });
            }, 2000);
            intervalArr.push(interval);
          });
        }),
      ]);
    }

    return {
      orderPromise: orderCheck,
      clearIntervalCb,
    };
  },
};
